import {
  ApiProperty,
  ApiPropertyOptional,
  IntersectionType,
} from '@nestjs/swagger';
import { Expose, Type } from 'class-transformer';
import {
  IsBoolean,
  IsEnum,
  IsInt,
  IsISO8601,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';

import { ArchivableEntityDto } from '../../common/dto/archivable-entity.dto';
import { BoardPurchaseDto } from '../../board-purchase/dto/board-purchase.dto';
import { BuildingDto } from '../../building/dto/building.dto';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import { JobInterface } from '../interfaces/job.interface';
import { JobOptionDto } from '../../job-option/dto/job-option.dto';
import { JobPhaseDto } from '../../job-phase/dto/job-phase.dto';
import { ModelDto } from '../../model/dto/model.dto';
import { UserCreatedDto } from '../../user/dto/user-created.dto';
import { UserUpdatedDto } from '../../user/dto/user-updated.dto';
import { UserDto } from '../../user/dto/user.dto';
import { JobRepairDto } from '../../job-repair/dto/job-repair.dto';
import { JobNoteDto } from '../../job-note/dtos/job-note.dto';
import { JobOriginalSupervisorInterface } from '../../job-original-supervisor/interfaces/job-original-supervisor.interface';
import { JobOriginalSupervisorDto } from '../../job-original-supervisor/dto/job-original-supervisor.dto';
import { PhaseStructureTypeEnum } from '../../phase/enums/phase-structure-type.enum';
import { GeographyPointDto } from '../../common/dto/geography-point.dto';

export class JobDto
  extends IntersectionType(
    IntersectionType(CommonEntityDto, ArchivableEntityDto),
    IntersectionType(UserCreatedDto, UserUpdatedDto),
  )
  implements JobInterface
{
  @ApiProperty({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'Date Approved',
    description: 'The date and time the job was approved',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateApproved!: Date | null;

  @ApiProperty({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'Date Started',
    description: 'The date and time the job was started',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateStart!: Date | null;

  @ApiProperty({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'Date Due',
    description: 'The date and time the job is due',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateDue!: Date | null;

  @ApiProperty({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'Date House Marked',
    description: 'The date and time the house was marked.',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateHouseMarked: Date | null = null;

  @ApiProperty({
    title: 'Id Number',
    description: 'The job number/identifier',
    minLength: 1,
    maxLength: 100,
    nullable: true,
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  idNumber!: string | null;

  @ApiProperty({
    title: 'Community Phase',
    description: 'The community phase',
    minLength: 1,
    maxLength: 100,
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  communityPhase!: string;

  @ApiProperty({
    title: 'Lot',
    description: 'The lot number/identifier',
    minLength: 1,
    maxLength: 100,
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  lot!: string;

  @ApiProperty({
    title: 'Block',
    description: 'The block number/identifier',
    minLength: 1,
    maxLength: 100,
    nullable: true,
  })
  @IsOptional()
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  block!: string;

  @ApiProperty({
    description: 'Address line one',
    minLength: 1,
    maxLength: 100,
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  address1!: string;

  @ApiProperty({
    description: 'Address line two',
    minLength: 1,
    maxLength: 100,
    nullable: true,
  })
  @IsString()
  @MaxLength(100)
  @Expose()
  address2!: string;

  @ApiProperty({
    description: 'City',
    minLength: 1,
    maxLength: 100,
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  city!: string;

  @ApiProperty({
    description: 'State',
    minLength: 1,
    maxLength: 100,
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  state!: string;

  @ApiProperty({
    description: 'ZIP code must be in the format 12345 or 12345-6789',
    example: '12345-6789',
  })
  @IsNotEmpty()
  @Matches(/^\d{5}(-\d{4})?$/, {
    message:
      'Zip code must be entered either in 5 digit format (12345) or 9 digit format (12345-1234)',
  })
  @Expose()
  zip!: string;

  @ApiProperty({
    title: 'Options',
    description: 'Options',
    minLength: 1,
    maxLength: 5000,
  })
  @IsString()
  @MinLength(1)
  @MaxLength(5000)
  @Expose()
  options!: string;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'voided?',
    description: 'Voided',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  voided!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'screwInspection?',
    description: 'screwInspection',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  screwInspection!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'isGarage?',
    description: 'isGarage',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  garageFirst!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'highCeiling?',
    description: 'highCeiling',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  highCeiling!: boolean;

  @ApiProperty({
    type: 'enum',
    enum: PhaseStructureTypeEnum,
    nullable: true,
    title: 'Job  Structure Type',
    description: 'Job  Structure Type',
  })
  @IsEnum(PhaseStructureTypeEnum)
  @Expose()
  phaseStructureType!: PhaseStructureTypeEnum;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'Is job voidable?',
    description: 'Will be set to true if it is possible to void the job.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  isVoidable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'House Marked?',
    description: 'Will be true if has has been marked.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  houseMarked = false;

  @ApiProperty({
    format: 'uuid',
    description: 'The user id that set the house as marked.',
    nullable: true,
  })
  @IsUUID()
  @Expose()
  houseMarkedByUserId: string | null = null;

  @ApiProperty({
    format: 'uuid',
    description: 'The initial supervisor user id at time of job creation.',
    nullable: true,
  })
  @IsUUID()
  @Expose()
  originalSupervisorUserId!: string;

  @ApiProperty({
    type: () => UserDto,
    description: 'The initial supervisor user at time of job creation.',
  })
  @Expose()
  originalSupervisorUser?: UserDto;

  @ApiProperty({
    format: 'uuid',
    title: 'Model ID',
    description: 'The ID of the model for this job',
  })
  @IsUUID()
  @Expose()
  modelId!: string;

  @ApiPropertyOptional({
    type: () => ModelDto,
    title: 'Associated Model',
    description: 'The model associated with this job',
  })
  @Expose()
  model?: ModelDto;

  @ApiPropertyOptional({
    format: 'uuid',
    title: 'Building ID',
    description: 'The ID of the building',
  })
  @IsUUID()
  @Expose()
  buildingId!: string | null;

  @ApiPropertyOptional({
    type: () => BuildingDto,
    title: 'Associated Building',
    description: 'The building associated with this job',
  })
  @Expose()
  building?: BuildingDto;

  @ApiPropertyOptional({
    nullable: true,
    format: 'uuid',
    title: 'Job Phase ID',
    description: 'The ID of the current job phase',
  })
  @IsUUID()
  @Expose()
  jobPhaseId!: string | null;

  @ApiPropertyOptional({
    type: () => [JobPhaseDto],
    title: 'Current Job Phase',
    description: 'The current Job Phase associated with this job',
  })
  @Expose()
  jobPhase?: JobPhaseDto;

  @ApiPropertyOptional({
    type: () => [BoardPurchaseDto],
    title: 'Associated Board Purchases',
    description: 'The board purchases associated with this job',
  })
  @Expose()
  boardPurchases?: BoardPurchaseDto[];

  @ApiPropertyOptional({
    type: () => [JobPhaseDto],
    title: 'Job Phases',
    description: 'The Job Phases associated with this job',
  })
  @Expose()
  jobPhases?: JobPhaseDto[];

  @ApiPropertyOptional({
    type: () => [JobOptionDto],
    title: 'Job Options',
    description: 'The Job Options associated with this job',
  })
  @Expose()
  jobOptions?: JobOptionDto[];

  @ApiProperty({
    title: 'Default Total Square Footage',
    description: 'Total square footage used by default.',
    default: null,
  })
  @IsOptional()
  @IsInt()
  @Min(1)
  @Expose()
  totalSquareFootage?: number;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'hasTrim?',
    description: 'hasTrim',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasTrim!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'hasInteriorOptions?',
    description: 'hasInteriorOptions',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasInteriorOptions!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'interiorOptionsFinished?',
    description: 'interiorOptionsFinished',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  interiorOptionsFinished!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'exteriorOptionsFinished?',
    description: 'exteriorOptionsFinished',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  exteriorOptionsFinished!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'false',
    title: 'trimOptionsFinished?',
    description: 'trimOptionsFinished',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  trimOptionsFinished!: boolean;

  @ApiProperty({
    title: 'Default interior Option Cost',
    description: 'Default cost for interior options.',
    default: null,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  interiorOptionCost?: number;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'hasExteriorOptions?',
    description: 'hasExteriorOptions',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasExteriorOptions!: boolean;

  @ApiProperty({
    title: 'Default exterior Option Cost',
    description: 'Default cost for exterior options.',
    default: null,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  exteriorOptionCost?: number;

  @ApiProperty({
    type: 'boolean',
    default: 'false',
    title: 'hasTrimOptions?',
    description: 'hasTrimOptions',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasTrimOptions!: boolean;

  @ApiProperty({
    title: 'Trim Option Cost',
    description: 'Cost for trim options.',
    default: null,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  trimOptionCost?: number;

  @ApiPropertyOptional({
    type: () => [JobRepairDto],
    title: 'Job Repair',
    description: 'The Job Repairs associated with this job',
  })
  @Expose()
  jobRepairs?: JobRepairDto[];

  @ApiPropertyOptional({
    nullable: true,
    format: 'uuid',
    title: 'Interior Options Finished Job Phase ID',
    description: 'The ID of the job phase when interior options were finished',
  })
  @IsUUID()
  @Expose()
  interiorOptionsFinishedJobPhaseId!: JobPhaseDto['id'] | null;

  @ApiPropertyOptional({
    nullable: true,
    format: 'uuid',
    title: 'Exterior Options Finished Job Phase ID',
    description: 'The ID of the job phase when exterior options were finished',
  })
  @IsUUID()
  @Expose()
  exteriorOptionsFinishedJobPhaseId!: JobPhaseDto['id'] | null;

  @ApiPropertyOptional({
    nullable: true,
    format: 'uuid',
    title: 'Trim Options Finished Job Phase ID',
    description: 'The ID of the job phase when trim options were finished',
  })
  @IsUUID()
  @Expose()
  trimOptionsFinishedJobPhaseId!: JobPhaseDto['id'] | null;

  @ApiPropertyOptional({
    type: () => [JobNoteDto],
    title: 'Job Notes',
    description: 'The Job notes associated with this job',
  })
  @Expose()
  jobNotes?: JobNoteDto[];

  @ApiPropertyOptional({
    type: () => [JobOriginalSupervisorDto],
    title: 'Job Original',
    description: 'Job Original',
  })
  @Expose()
  jobOriginalSupervisorInterface?: JobOriginalSupervisorInterface[];

  @ApiPropertyOptional({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'Last Event At',
    description: 'The date and time of the last event/activity on this job',
  })
  @IsOptional()
  @IsISO8601({ strict: true })
  @Expose()
  lastEventAt?: Date | null;

  @ApiPropertyOptional({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'Archived At',
    description: 'The date and time when this job was archived',
  })
  @IsOptional()
  @IsISO8601({ strict: true })
  @Expose()
  archivedAt?: Date | null;

  @ApiPropertyOptional({
    type: 'boolean',
    nullable: true,
    title: 'Active',
    description: 'Indicates if the job is currently active (signed off)',
  })
  @IsOptional()
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  active?: boolean | null;

  @ApiPropertyOptional({
    type: 'string',
    format: 'uuid',
    nullable: true,
    title: 'Starting Phase ID',
    description:
      'The ID of the phase where this job should start (skipping previous phases)',
  })
  @IsOptional()
  @IsUUID()
  @Expose()
  startingPhaseId?: string | null;

  @ApiPropertyOptional({
    type: 'boolean',
    nullable: true,
    title: 'Starting Phase Approved',
    description:
      'When true, the starting phase should be considered already approved upon creation',
  })
  @IsOptional()
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  startingPhaseApproved?: boolean | null;

  @ApiPropertyOptional({
    type: () => GeographyPointDto,
    nullable: true,
    default: null,
    title: 'Geo',
    description: 'GPS location of this job.',
  })
  @IsOptional()
  @Expose()
  geo?: GeographyPointDto | null;

  @ApiPropertyOptional({
    title: 'Geo Distance Tolerance',
    description:
      'Distance tolerance in meters for geolocation matching. Overrides subdivision value when set.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  geoDistanceTolerance?: number | null;
}
